<!-- SPDX-License-Identifier: AGPL-3.0-only -->

<script setup lang="ts">
import { computed, onMounted, ref, type ComputedRef, type Ref } from "vue";
import {
  createFirmwareRelease,
  firmwareReleaseInputFromStrings,
  listFirmwareProbeTypes,
  listFirmwareReleases,
  publishFirmwareRelease,
  revokeFirmwareRelease,
  type FirmwareChannel,
  type FirmwareRelease,
  type FirmwareProbeType,
} from "../api/firmware.js";
import AppShell from "../components/AppShell.vue";
import UserActionIcon from "../components/UserActionIcon.vue";
import { confirmDialog } from "../dialogs/app-dialog.js";
import { formatDateTime } from "../formatting/date-time.js";
import { useUserSettingsStore } from "../stores/user-settings.js";

const userSettingsStore: ReturnType<typeof useUserSettingsStore> = useUserSettingsStore();
const releases: Ref<FirmwareRelease[]> = ref([]);
const probeTypes: Ref<FirmwareProbeType[]> = ref([]);
const loading: Ref<boolean> = ref(false);
const busyReleaseId: Ref<string | null> = ref(null);
const loadingError: Ref<string | null> = ref(null);
const formMessage: Ref<string | null> = ref(null);
const formFailed: Ref<boolean> = ref(false);
const creating: Ref<boolean> = ref(false);
const channel: Ref<FirmwareChannel> = ref("stable");
const selectedProbeTypeId: Ref<string> = ref("");
const artifactFile: Ref<File | null> = ref(null);
const artifactVersionPreview: Ref<string | null> = ref(null);
const artifactProjectPreview: Ref<string | null> = ref(null);
const artifactPreviewError: Ref<string | null> = ref(null);
const mandatory: Ref<boolean> = ref(false);
const signature: Ref<string> = ref("");
const releaseNotes: Ref<string> = ref("");

const publishedCount: ComputedRef<number> = computed(
  (): number => releases.value.filter((release: FirmwareRelease): boolean => release.status === "published").length,
);
const draftCount: ComputedRef<number> = computed(
  (): number => releases.value.filter((release: FirmwareRelease): boolean => release.status === "draft").length,
);
const selectedTarget: ComputedRef<FirmwareProbeType | null> = computed(
  (): FirmwareProbeType | null => probeTypes.value.find((probeType: FirmwareProbeType): boolean => probeType.id === selectedProbeTypeId.value) ?? null,
);

function readNullTerminatedAscii(data: Uint8Array, offset: number, length: number): string {
  const endOffset: number = Math.min(offset + length, data.length);
  let terminatorOffset: number = offset;
  while (terminatorOffset < endOffset && data[terminatorOffset] !== 0) {
    terminatorOffset += 1;
  }
  return String.fromCharCode(...data.slice(offset, terminatorOffset)).trim();
}

function readEspFirmwarePreview(data: Uint8Array): { readonly version: string; readonly projectName: string } {
  const searchLimit: number = Math.min(4096, data.length - 112);
  for (let offset: number = 0; offset < searchLimit; offset += 1) {
    if (
      data[offset] === 0x32 &&
      data[offset + 1] === 0x54 &&
      data[offset + 2] === 0xcd &&
      data[offset + 3] === 0xab
    ) {
      const version: string = readNullTerminatedAscii(data, offset + 16, 32);
      const projectName: string = readNullTerminatedAscii(data, offset + 48, 32);
      if (version === "" || projectName === "") {
        throw new Error("Firmware metadata is incomplete.");
      }
      return { version, projectName };
    }
  }
  throw new Error("No ESP firmware metadata found.");
}

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : "Firmware request failed.";
}

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MiB`;
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(1)} KiB`;
  }
  return `${bytes} B`;
}

async function selectArtifactFile(event: Event): Promise<void> {
  const input: HTMLInputElement | null = event.target instanceof HTMLInputElement ? event.target : null;
  const selectedFile: File | null = input?.files?.[0] ?? null;
  artifactFile.value = selectedFile;
  artifactVersionPreview.value = null;
  artifactProjectPreview.value = null;
  artifactPreviewError.value = null;
  if (selectedFile === null) {
    return;
  }
  try {
    const data: Uint8Array = new Uint8Array(await selectedFile.arrayBuffer());
    const preview: { readonly version: string; readonly projectName: string } = readEspFirmwarePreview(data);
    artifactVersionPreview.value = preview.version;
    artifactProjectPreview.value = preview.projectName;
  } catch (error: unknown) {
    artifactPreviewError.value = error instanceof Error ? error.message : "Firmware metadata could not be read.";
  }
}

function statusClass(release: FirmwareRelease): string {
  if (release.status === "published") {
    return "status-badge status-badge--active";
  }
  if (release.status === "revoked") {
    return "status-badge status-badge--disabled";
  }
  return "status-badge status-badge--pending";
}

function resetForm(): void {
  channel.value = "stable";
  selectedProbeTypeId.value = probeTypes.value[0]?.id ?? "";
  artifactFile.value = null;
  artifactVersionPreview.value = null;
  artifactProjectPreview.value = null;
  artifactPreviewError.value = null;
  mandatory.value = false;
  signature.value = "";
  releaseNotes.value = "";
  formMessage.value = null;
  formFailed.value = false;
}

async function loadReleases(): Promise<void> {
  loading.value = true;
  loadingError.value = null;
  try {
    const loadedProbeTypes: readonly FirmwareProbeType[] = await listFirmwareProbeTypes();
    probeTypes.value = [...loadedProbeTypes];
    selectedProbeTypeId.value = selectedProbeTypeId.value || probeTypes.value[0]?.id || "";
    releases.value = [...await listFirmwareReleases()];
  } catch (error: unknown) {
    loadingError.value = errorText(error);
  } finally {
    loading.value = false;
  }
}

async function createRelease(): Promise<void> {
  creating.value = true;
  formMessage.value = null;
  formFailed.value = false;
  try {
    const selectedArtifactFile: File | null = artifactFile.value;
    if (selectedArtifactFile === null) {
      throw new Error("Select a firmware artifact file.");
    }
    const target: FirmwareProbeType | null = selectedTarget.value;
    if (target === null) throw new Error("Select a probe target.");
    if (artifactVersionPreview.value === null) {
      throw new Error("The selected firmware artifact has no readable ESP version metadata.");
    }
    await createFirmwareRelease(firmwareReleaseInputFromStrings({
      channel: channel.value,
      probeType: target.id,
      artifactFile: selectedArtifactFile,
      mandatory: mandatory.value,
      signature: signature.value,
      releaseNotes: releaseNotes.value,
    }));
    resetForm();
    formMessage.value = "Firmware release draft created.";
    await loadReleases();
  } catch (error: unknown) {
    formFailed.value = true;
    formMessage.value = errorText(error);
  } finally {
    creating.value = false;
  }
}

async function publishRelease(release: FirmwareRelease): Promise<void> {
  busyReleaseId.value = release.id;
  loadingError.value = null;
  try {
    await publishFirmwareRelease(release.id);
    await loadReleases();
  } catch (error: unknown) {
    loadingError.value = errorText(error);
  } finally {
    busyReleaseId.value = null;
  }
}

async function revokeRelease(release: FirmwareRelease): Promise<void> {
  const confirmed: boolean = await confirmDialog({
    title: "Revoke firmware release?",
    message: `Firmware ${release.version} on ${release.channel} will no longer be assigned to probes. Pending manual requests for this release will be superseded.`,
    confirmLabel: "Revoke release",
    tone: "danger",
  });
  if (!confirmed) {
    return;
  }
  busyReleaseId.value = release.id;
  loadingError.value = null;
  try {
    await revokeFirmwareRelease(release.id);
    await loadReleases();
  } catch (error: unknown) {
    loadingError.value = errorText(error);
  } finally {
    busyReleaseId.value = null;
  }
}

onMounted(loadReleases);
</script>

<template>
  <AppShell>
    <header class="page-heading">
      <div>
        <p class="eyebrow">System administration</p>
        <h1>Firmware</h1>
        <p>Manage server-approved firmware releases. Tenants and probes can only roll out published releases within their configured channel.</p>
      </div>
      <button class="button button--quiet page-heading__action" type="button" :disabled="loading" @click="loadReleases">
        Refresh
      </button>
    </header>

    <p v-if="loadingError" class="form-error" role="alert">{{ loadingError }}</p>

    <section class="metric-grid metric-grid--three" aria-label="Firmware summary">
      <article class="metric-card">
        <span class="metric-card__label">Releases</span>
        <strong>{{ releases.length }}</strong>
        <span>Total release metadata entries</span>
      </article>
      <article class="metric-card">
        <span class="metric-card__label">Published</span>
        <strong>{{ publishedCount }}</strong>
        <span>Eligible for rollout</span>
      </article>
      <article class="metric-card">
        <span class="metric-card__label">Drafts</span>
        <strong>{{ draftCount }}</strong>
        <span>Not yet eligible</span>
      </article>
    </section>

    <section class="content-panel settings-card">
      <div class="settings-card__heading">
        <div>
          <p class="eyebrow">Release metadata</p>
          <h2>Create draft</h2>
        </div>
      </div>
      <form class="settings-form settings-form--firmware" @submit.prevent="createRelease">
        <label>
          <span>Version</span>
          <input :value="artifactVersionPreview ?? ''" readonly placeholder="Select a firmware package" />
        </label>
        <label><span>Channel</span><select v-model="channel"><option value="stable">Stable</option><option value="beta">Beta</option></select></label>
        <label>
          <span>Probe target</span>
          <select v-model="selectedProbeTypeId" :disabled="probeTypes.length === 0">
            <option v-for="target in probeTypes" :key="target.id" :value="target.id">
              {{ target.displayName }}
            </option>
          </select>
        </label>
        <label><span>Platform</span><input :value="selectedTarget?.platform ?? ''" readonly /></label>
        <label><span>Hardware model</span><input :value="selectedTarget?.hardwareModel ?? ''" readonly /></label>
        <label><span>Architecture</span><input :value="selectedTarget?.architecture ?? ''" readonly /></label>
        <label class="settings-field--wide">
          <span>Firmware package</span>
          <input required type="file" accept=".bin,.img,application/octet-stream" @change="selectArtifactFile" />
          <small v-if="artifactFile">{{ artifactFile.name }} · {{ formatSize(artifactFile.size) }}</small>
          <small v-else>Upload the ESP OTA image binary. The server calculates size and SHA-256.</small>
          <small v-if="artifactPreviewError" class="form-error">{{ artifactPreviewError }}</small>
        </label>
        <label class="settings-checkbox settings-checkbox--wide"><input v-model="mandatory" type="checkbox" /><span>Mandatory update for compatibility or security</span></label>
        <label class="settings-field--wide"><span>Signature · optional</span><textarea v-model="signature" rows="3" placeholder="Detached signature or signature metadata"></textarea></label>
        <label class="settings-field--wide"><span>Release notes · optional</span><textarea v-model="releaseNotes" rows="4" placeholder="Operational notes for this firmware"></textarea></label>
        <p v-if="formMessage" :class="formFailed ? 'form-error' : 'settings-message'" :role="formFailed ? 'alert' : 'status'">{{ formMessage }}</p>
        <button class="button button--primary settings-field--wide" type="submit" :disabled="creating">
          {{ creating ? "Creating…" : "Create draft release" }}
        </button>
      </form>
    </section>

    <section class="content-panel data-table-panel">
      <div class="content-panel__heading">
        <div>
          <p class="eyebrow">Catalog</p>
          <h2>Firmware releases</h2>
        </div>
      </div>
      <p v-if="loading" class="placeholder-panel">Loading firmware releases…</p>
      <p v-else-if="releases.length === 0" class="placeholder-panel">No firmware releases exist yet.</p>
      <div v-else class="data-table data-table--firmware" role="table" aria-label="Firmware releases">
        <div class="data-table__header" role="row">
          <span role="columnheader">Release</span>
          <span role="columnheader">Target</span>
          <span role="columnheader">Artifact</span>
          <span role="columnheader">Lifecycle</span>
          <span role="columnheader">Actions</span>
        </div>
        <div v-for="release in releases" :key="release.id" class="data-table__row" role="row">
          <span role="cell">
            <strong>{{ release.version }}</strong>
            <small>{{ release.channel }} · {{ release.id }}</small>
          </span>
          <span role="cell">
            <strong>{{ release.probeType }}</strong>
            <small>Compatibility is defined by the probe type.</small>
          </span>
          <span role="cell">
            <strong>{{ formatSize(release.sizeBytes) }}</strong>
            <small>{{ release.sha256.slice(0, 12) }}… · {{ release.artifactUrl }}</small>
          </span>
          <span role="cell">
            <span :class="release.mandatory ? 'status-badge status-badge--mandatory' : statusClass(release)">
              {{ release.mandatory ? "mandatory" : release.status }}
            </span>
            <small v-if="release.publishedAt">published {{ formatDateTime(release.publishedAt, userSettingsStore.settings.dateTimeFormat) }}</small>
            <small v-else>created {{ formatDateTime(release.createdAt, userSettingsStore.settings.dateTimeFormat) }}</small>
          </span>
          <span role="cell" class="row-actions">
            <button
              v-if="release.status === 'draft'"
              class="icon-button"
              type="button"
              :disabled="busyReleaseId === release.id"
              title="Publish release"
              :aria-label="`Publish firmware ${release.version} on ${release.channel}`"
              @click="publishRelease(release)"
            >
              <UserActionIcon name="publish" />
            </button>
            <button
              v-if="release.status !== 'revoked'"
              class="icon-button icon-button--danger"
              type="button"
              :disabled="busyReleaseId === release.id"
              title="Revoke release"
              :aria-label="`Revoke firmware ${release.version} on ${release.channel}`"
              @click="revokeRelease(release)"
            >
              <UserActionIcon name="revoke" />
            </button>
          </span>
        </div>
      </div>
    </section>
  </AppShell>
</template>
